import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const galleryImages = [
  {
    src: "https://images.pexels.com/photos/8470881/pexels-photo-8470881.jpeg?auto=compress&cs=tinysrgb&w=800&h=800&fit=crop",
    alt: "Lackierer sprüht Lack auf Fahrzeug",
    caption: "Professionelle Fahrzeuglackierung"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/i35efism_Autolackierer_bearbeitet.jpg",
    alt: "Autolackierer bei der Arbeit",
    caption: "Präzise Lackierarbeiten"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/afpfrzcu_Schwarz_bearbeitet.jpg",
    alt: "Schwarzer Lack wird aufgetragen",
    caption: "Hochwertige Lacke"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/kj811n6n_Teamfoto_bearbeitet.jpg",
    alt: "Unser Team",
    caption: "Unser erfahrenes Team"
  },
  {
    src: "https://images.pexels.com/photos/5233262/pexels-photo-5233262.jpeg?auto=compress&cs=tinysrgb&w=800&h=800&fit=crop",
    alt: "Lackpflege und Politur",
    caption: "Professionelle Fahrzeugaufbereitung"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/8n2qmncb_Prozess_Lackierens_bearbeitet-e1678275803600.jpg",
    alt: "Lackierprozess",
    caption: "Sorgfältiger Lackierprozess"
  }
];

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const navigateImage = (direction) => {
    if (selectedImage === null) return;
    
    const currentIndex = galleryImages.findIndex(img => img.src === selectedImage.src);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % galleryImages.length;
    } else {
      newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    }
    
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <section
      id="galerie"
      ref={sectionRef}
      className="py-20 lg:py-28 bg-background"
    >
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
            Galerie
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Einblicke in unsere Arbeit
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Qualität, die man sieht. Entdecken Sie Impressionen aus unserem Betrieb 
            und überzeugen Sie sich von unserer sorgfältigen Arbeitsweise.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${isVisible ? 'stagger-children' : ''}`}>
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-xl cursor-pointer card-hover aspect-square"
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <p className="text-sm font-medium text-background">
                  {image.caption}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox Dialog */}
        <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors"
                aria-label="Schließen"
              >
                <X className="h-5 w-5 text-foreground" />
              </button>
              
              <button
                onClick={() => navigateImage('prev')}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors"
                aria-label="Vorheriges Bild"
              >
                <ChevronLeft className="h-5 w-5 text-foreground" />
              </button>
              
              <button
                onClick={() => navigateImage('next')}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors"
                aria-label="Nächstes Bild"
              >
                <ChevronRight className="h-5 w-5 text-foreground" />
              </button>

              {selectedImage && (
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="w-full h-auto max-h-[80vh] object-contain bg-background"
                  />
                  <div className="bg-background p-4">
                    <p className="text-foreground font-medium text-center">
                      {selectedImage.caption}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default Gallery;
